import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { teacherStudentsApi } from '../../services/teacherStudentsApi';
import { faculties, specialtiesData, academicLevels } from '../../data/espData';
import { getSafeAvatarUrl } from '../../utils/avatarPlaceholder';

export const LANGUAGES = ['English', 'French', 'Spanish', 'German', 'Italian'];
const SCHOOLS = [
  { id: 'Primary School', years: ['First Year', 'Second Year', 'Third Year'] },
  { id: 'Middle School', years: ['First Year', 'Second Year', 'Third Year', 'Fourth Year'] },
  { id: 'High School', years: ['First Year', 'Second Year', 'Third Year'] }
];
const EXAMS = {
  English: { IELTS: ['Academic', 'General Training', 'UKVI'], TOEFL: ['iBT', 'PBT', 'CBT'], CAMBRIDGE: ['FCE', 'CAE', 'CPE', 'PET', 'KET'] },
  French: { DELF_DALF: [], TCF: [], TEF: [], DCL: [] },
  Spanish: { DELE: [], SIELE: [], CELU: [] },
  German: { GOETHE_ZERTIFIKAT: [], TESTDAF: [], TELC_GERMAN: [] },
  Italian: { CILS: [], CELI: [], PLIDA: [] }
};
const CEFR = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const READING_TYPES = ['levels', 'exam', 'esp', 'general'];

export function initials(name) {
  const parts = String(name || '').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'S').slice(0, 2).toUpperCase();
}

export function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(value);
  }
}

export function StatusBadge({ status }) {
  const { t } = useTranslation('teacher');
  return (
    <span className={`status-badge ${status || 'not_started'}`}>
      {t(`myStudents.status.${status || 'not_started'}`)}
    </span>
  );
}

export function LoadError({ message, onRetry }) {
  const { t } = useTranslation('teacher');
  return (
    <div className="teacher-section">
      <div className="teacher-section-header">
        <div className="teacher-section-title">
          <i className="material-icons">error_outline</i>
          {t('myStudents.unableToLoad')}
        </div>
      </div>
      <p className="teacher-section-desc">{message}</p>
      <button type="button" className="teacher-btn primary" onClick={onRetry}>
        <i className="material-icons">refresh</i>
        {t('myStudents.retry')}
      </button>
    </div>
  );
}

export function Avatar({ avatar, name }) {
  const letter = initials(name);
  if (!avatar) return <div className="student-avatar">{letter}</div>;
  return (
    <div className="student-avatar">
      <img src={getSafeAvatarUrl(avatar, letter)} alt="" />
    </div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav className="teacher-breadcrumbs" aria-label="Breadcrumb">
      {items.map((item, idx) => (
        <React.Fragment key={`${item.label}-${idx}`}>
          {idx > 0 && <span>→</span>}
          {item.onClick && idx < items.length - 1 ? (
            <button type="button" onClick={item.onClick}>{item.label}</button>
          ) : (
            <span className={idx === items.length - 1 ? 'current' : ''}>{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function Select({ value, onChange, children }) {
  return (
    <select className="teacher-select" value={value} onChange={onChange}>
      {children}
    </select>
  );
}

export function ContextFields({ pathId, context, setContext }) {
  const { t } = useTranslation('teacher');
  const set = (key, value) => setContext((prev) => ({ ...prev, [key]: value }));
  const exams = EXAMS[context.language] || EXAMS.English;
  const examTypes = Object.keys(exams);
  const categories = exams[context.exam_type] || [];
  const school = SCHOOLS.find((s) => s.id === context.school);
  const faculty = faculties.find((f) => f.label === context.faculty || f.id === context.faculty);
  const lang = (
    <Select value={context.language || ''} onChange={(e) => set('language', e.target.value)}>
      <option value="">{t('myStudents.language')}</option>
      {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
    </Select>
  );
  if (pathId === 'educational-levels-vocabulary') {
    return (
      <>
        {lang}
        <Select value={context.school || ''} onChange={(e) => setContext((p) => ({ ...p, school: e.target.value, year: '' }))}>
          <option value="">{t('myStudents.school')}</option>
          {SCHOOLS.map((s) => <option key={s.id} value={s.id}>{s.id}</option>)}
        </Select>
        <Select value={context.year || ''} onChange={(e) => set('year', e.target.value)}>
          <option value="">{t('myStudents.year')}</option>
          {(school?.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
        </Select>
      </>
    );
  }
  if (pathId === 'international-exam-vocabulary') {
    return (
      <>
        {lang}
        <Select value={context.exam_type || ''} onChange={(e) => setContext((p) => ({ ...p, exam_type: e.target.value, category: '' }))}>
          <option value="">{t('myStudents.examType')}</option>
          {examTypes.map((e) => <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>)}
        </Select>
        {categories.length > 0 && (
          <Select value={context.category || ''} onChange={(e) => set('category', e.target.value)}>
            <option value="">{t('myStudents.category')}</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        )}
      </>
    );
  }
  if (pathId === 'general-vocabulary') {
    return (
      <>
        {lang}
        <Select value={context.cefr_level || ''} onChange={(e) => set('cefr_level', e.target.value)}>
          <option value="">{t('myStudents.cefr')}</option>
          {CEFR.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
      </>
    );
  }
  if (pathId === 'academic-vocabulary-esp') {
    return (
      <>
        {lang}
        <Select value={context.faculty || ''} onChange={(e) => setContext((p) => ({ ...p, faculty: e.target.value, specialty: '' }))}>
          <option value="">{t('myStudents.faculty')}</option>
          {faculties.map((f) => <option key={f.id} value={f.label}>{f.label}</option>)}
        </Select>
        <Select value={context.specialty || ''} onChange={(e) => set('specialty', e.target.value)}>
          <option value="">{t('myStudents.specialty')}</option>
          {(specialtiesData[faculty?.id] || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Select value={context.academic_year || ''} onChange={(e) => set('academic_year', e.target.value)}>
          <option value="">{t('myStudents.academicYear')}</option>
          {academicLevels.map((a) => <option key={a.id} value={a.label}>{a.label}</option>)}
        </Select>
      </>
    );
  }
  if (pathId === 'dictation') {
    return (
      <>
        {lang}
        <Select value={context.school_level || ''} onChange={(e) => setContext((p) => ({ ...p, school_level: e.target.value, year: '' }))}>
          <option value="">{t('myStudents.schoolLevel')}</option>
          <option value="primary">Primary</option>
          <option value="middle">Middle</option>
          <option value="high">High</option>
        </Select>
        <Select value={context.year || ''} onChange={(e) => set('year', e.target.value)}>
          <option value="">{t('myStudents.year')}</option>
          {['1', '2', '3', '4'].map((y) => <option key={y} value={y}>{y}</option>)}
        </Select>
      </>
    );
  }
  if (pathId === 'reading') {
    return (
      <>
        {lang}
        <Select value={context.path_type || ''} onChange={(e) => set('path_type', e.target.value)}>
          <option value="">{t('myStudents.readingType')}</option>
          {READING_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
        </Select>
      </>
    );
  }
  return lang;
}

export function GroupModal({ pathId, courseId, onClose, onCreated }) {
  const { t } = useTranslation('teacher');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [context, setContext] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = { name, description, context };
      const data = pathId
        ? await teacherStudentsApi.createPathGroup(pathId, body)
        : await teacherStudentsApi.createCourseGroup(courseId, body);
      onCreated(data.group);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="teacher-modal-overlay" onClick={onClose}>
      <div className="teacher-modal-content" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="teacher-modal-close" onClick={onClose}>×</button>
        <h2>{t('myStudents.createGroup')}</h2>
        <form className="teacher-form" onSubmit={submit}>
          <div className="teacher-form-group">
            <label>{t('myStudents.groupName')}</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} placeholder={t('myStudents.groupName')} />
          </div>
          <div className="teacher-form-group">
            <label>{t('myStudents.descriptionOptional')}</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('myStudents.descriptionOptional')} rows={3} />
          </div>
          {pathId && (
            <div className="teacher-form-group">
              <label>{t('myStudents.category')}</label>
              <div className="teacher-filter-group">
                <ContextFields pathId={pathId} context={context} setContext={setContext} />
              </div>
            </div>
          )}
          {error && <p className="teacher-section-desc">{error}</p>}
          <div className="teacher-form-actions">
            <button type="button" className="teacher-btn secondary" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="teacher-btn primary" disabled={saving}>
              <i className="material-icons">group_add</i>
              {t('myStudents.createGroup')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AddStudentModal({ pathId, courseId, groups, onClose, onAdded }) {
  const { t } = useTranslation('teacher');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [groupId, setGroupId] = useState(groups[0]?.id || '');
  const [newGroupName, setNewGroupName] = useState('');
  const [context, setContext] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await teacherStudentsApi.searchStudents(query.trim());
        setResults(data.students || []);
      } catch (err) {
        setError(err.message);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      let targetGroupId = groupId;
      if (!targetGroupId) {
        if (!newGroupName.trim()) {
          setError(t('myStudents.groupRequired'));
          setSaving(false);
          return;
        }
        const created = pathId
          ? await teacherStudentsApi.createPathGroup(pathId, { name: newGroupName.trim(), context })
          : await teacherStudentsApi.createCourseGroup(courseId, { name: newGroupName.trim() });
        targetGroupId = created.group.id;
      }
      await teacherStudentsApi.addStudent(targetGroupId, selected.id);
      onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="teacher-modal-overlay" onClick={onClose}>
      <div className="teacher-modal-content" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="teacher-modal-close" onClick={onClose}>×</button>
        <h2>{t('myStudents.addStudent')}</h2>
        <form className="teacher-form" onSubmit={submit}>
          <div className="teacher-form-group">
            <label>{t('myStudents.searchStudentsHint')}</label>
            <div className="teacher-search">
              <i className="material-icons">search</i>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('myStudents.searchStudentsHint')} />
            </div>
          </div>
          {searching && <p className="teacher-content-subtitle">{t('common.loading')}</p>}
          <div className="teacher-search-results">
            {results.map((s) => (
              <button
                type="button"
                key={s.id}
                className={`teacher-action-card${selected?.id === s.id ? ' selected' : ''}`}
                onClick={() => setSelected(s)}
              >
                <Avatar avatar={s.avatar} name={s.name} />
                <div className="action-content">
                  <h4>{s.name}</h4>
                  <p>{s.email}</p>
                </div>
              </button>
            ))}
          </div>
          <div className="teacher-form-group">
            <label>{t('myStudents.selectOrCreateGroup')}</label>
            <select className="teacher-select" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">{t('myStudents.selectOrCreateGroup')}</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          {!groupId && (
            <>
              <div className="teacher-form-group">
                <label>{t('myStudents.newGroupName')}</label>
                <input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder={t('myStudents.newGroupName')} />
              </div>
              {pathId && (
                <div className="teacher-form-group">
                  <label>{t('myStudents.category')}</label>
                  <div className="teacher-filter-group">
                    <ContextFields pathId={pathId} context={context} setContext={setContext} />
                  </div>
                </div>
              )}
            </>
          )}
          {error && <p className="teacher-section-desc">{error}</p>}
          <div className="teacher-form-actions">
            <button type="button" className="teacher-btn secondary" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="teacher-btn primary" disabled={!selected || saving}>
              <i className="material-icons">person_add</i>
              {t('myStudents.confirmAdd')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function OverviewCard({ title, value, icon, variant, change, changeIcon }) {
  return (
    <div className={`teacher-overview-card ${variant || ''}`.trim()}>
      <div className="teacher-overview-header">
        <span className="teacher-overview-title">{title}</span>
        <div className="teacher-overview-icon">
          <i className="material-icons">{icon}</i>
        </div>
      </div>
      <div className="teacher-overview-value">{value}</div>
      {change != null && (
        <div className="teacher-overview-change">
          <i className="material-icons">{changeIcon || 'groups'}</i>
          {change}
        </div>
      )}
    </div>
  );
}
